import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { SectionCard } from '../../../components/admin';
import { useNavigate } from 'react-router-dom';
import { getCameraList } from '../../../services/cameraService';
import { resolveStatusToken } from '../../../constants/statusTokens';

const metricTileStyles = {
  px: 2.5,
  py: 2,
  borderRadius: 2,
  border: '1px solid rgba(122, 59, 193, 0.08)',
  bgcolor: '#F9FAFF',
};

const MAX_WATCHLIST_ITEMS = 4;
const STALE_HEARTBEAT_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

const LiveMonitoringSection = ({ stats, events = [], navigate: navigateProp }) => {
  const fallbackNavigate = useNavigate();
  const navigate = navigateProp || fallbackNavigate;

  const [cameraSummary, setCameraSummary] = useState({
    total: stats?.cameras ?? 0,
    active: 0,
    offline: 0,
    awaiting: 0,
    lastHealthCheck: null,
  });
  const [loadingCameras, setLoadingCameras] = useState(true);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchCameras = async () => {
      setLoadingCameras(true);
      setCameraError('');
      try {
        const cameraList = await getCameraList();
        if (!isMounted) return;

        const total = cameraList.length || stats?.cameras || 0;
        const now = Date.now();
        let active = 0;
        let awaiting = 0;
        let latestPing = null;

        cameraList.forEach((camera) => {
          const ping = camera.last_health_check ? new Date(camera.last_health_check).getTime() : 0;
          if (ping) {
            latestPing = latestPing ? Math.max(latestPing, ping) : ping;
          }
          const isReachable = camera.is_active !== false;
          const isStale = !ping || now - ping > STALE_HEARTBEAT_THRESHOLD_MS;
          if (isReachable && !isStale) {
            active += 1;
          } else if (isReachable && isStale) {
            awaiting += 1;
          }
        });

        const offline = Math.max(total - active - awaiting, 0);
        const lastHealthCheckDate = latestPing ? new Date(latestPing) : null;

        setCameraSummary({
          total,
          active,
          offline,
          awaiting,
          lastHealthCheck: lastHealthCheckDate,
        });
      } catch (err) {
        if (!isMounted) return;
        setCameraError('Unable to fetch camera health right now.');
        setCameraSummary((prev) => ({
          ...prev,
          total: stats?.cameras ?? prev.total,
        }));
      } finally {
        if (isMounted) {
          setLoadingCameras(false);
        }
      }
    };

    fetchCameras();
    const intervalId = setInterval(fetchCameras, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [stats?.cameras]);

  const watchlist = useMemo(() => {
    if (!Array.isArray(events)) {
      return [];
    }
    return events
      .filter((event) => event.status && !['CLOSED', 'ADMIN_REVIEWED'].includes(event.status))
      .slice(0, MAX_WATCHLIST_ITEMS)
      .map((event) => ({
        id: event.id,
        cameraName: event.camera?.name || event.camera_name || 'Unknown camera',
        status: event.status,
        timestamp: event.timestamp || event.detected_at,
      }));
  }, [events]);

  const latestIncident = useMemo(() => {
    const event = events.find((item) => item.timestamp);
    if (!event) return null;
    return new Date(event.timestamp);
  }, [events]);

  const renderCount = (value, fallback = '-') => (
    loadingCameras ? <CircularProgress size={22} /> : <Typography variant="h5" fontWeight={700}>{value ?? fallback}</Typography>
  );

  return (
    <SectionCard
      title="Monitoring summary"
      description="Track camera health and unresolved incidents. Open the monitoring workspace to watch live feeds."
      actions={(
        <Button
          variant="contained"
          color="primary"
          startIcon={<PlayCircleOutlineIcon />}
          onClick={() => navigate('/monitoring')}
        >
          Open monitoring workspace
        </Button>
      )}
      contentProps={{ sx: { gap: 3 } }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Stack sx={metricTileStyles} spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SensorsIcon color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Active cameras
              </Typography>
            </Stack>
            {renderCount(
              `${cameraSummary.active} / ${cameraSummary.total}`,
              `${stats?.cameras ?? 0}`,
            )}
            <Typography variant="body2" color="text.secondary">
              Cameras currently reporting in real-time.
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Stack sx={metricTileStyles} spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <VideocamOffIcon color={cameraSummary.offline > 0 ? 'warning' : 'action'} />
              <Typography variant="subtitle2" color="text.secondary">
                Cameras offline
              </Typography>
            </Stack>
            {renderCount(cameraSummary.offline)}
            <Typography variant="body2" color="text.secondary">
              Units needing intervention to restore coverage.
            </Typography>
            {cameraSummary.awaiting > 0 && (
              <Typography variant="caption" color="text.secondary">
                {cameraSummary.awaiting} reachable but awaiting motion heartbeat.
              </Typography>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Stack sx={metricTileStyles} spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Latest activity
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {cameraSummary.lastHealthCheck
                ? cameraSummary.lastHealthCheck.toLocaleString()
                : latestIncident
                  ? latestIncident.toLocaleString()
                  : 'No recent health checks'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Most recent camera health check or fall detection.
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      {cameraError && (
        <Typography variant="body2" color="error">
          {cameraError}
        </Typography>
      )}

      <Divider />

      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          Priority watchlist
        </Typography>
        {watchlist.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No unresolved incidents at the moment. New alerts will appear here as soon as they are detected.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {watchlist.map((item) => {
              const token = resolveStatusToken(item.status, 'fallEvents');
              const label = `${item.cameraName} \u2022 ${token?.label || item.status}`;
              return (
                <Chip
                  key={item.id}
                  label={label}
                  variant="outlined"
                  color={token?.color === 'default' ? 'primary' : token?.color}
                  onClick={() => navigate('/monitoring')}
                  sx={{ borderRadius: 2 }}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

export default LiveMonitoringSection;
